/*
 * Раздел «Задачи»: подписи, сортировка, сроки и календарная сетка.
 * Здесь только расчёты — без запросов к серверу и без разметки.
 */

export const getId = (value) => String(value?._id || value?.id || value || "");

export const STATUSES = [
    { value: "todo", label: "Не начата" },
    { value: "in_progress", label: "В работе" },
    { value: "done", label: "Готово" },
];

export const statusLabel = (status) => (
    STATUSES.find((item) => item.value === status)?.label || "Не начата"
);

/** По нажатию на кружок: не начата → в работе → готово → снова не начата. */
export const nextStatus = (status) => (
    status === "todo" ? "in_progress" : status === "in_progress" ? "done" : "todo"
);

export const REMIND_OPTIONS = [
    { value: -1, label: "Не напоминать" },
    { value: 0, label: "В срок" },
    { value: 15, label: "За 15 минут" },
    { value: 60, label: "За час" },
    { value: 180, label: "За 3 часа" },
    { value: 1440, label: "За день" },
];

export const REPEAT_OPTIONS = [
    { value: "none", label: "Не повторять" },
    { value: "day", label: "Каждый день" },
    { value: "week", label: "Каждую неделю" },
    { value: "month", label: "Каждый месяц" },
];

export const repeatLabel = (value) => (
    REPEAT_OPTIONS.find((item) => item.value === value)?.label || "Не повторять"
);

/** Имя человека; себя показываем как «Вы». */
export const userName = (user, meId = "", fallback = "Участник") => {
    if (!user) return fallback;
    if (meId && getId(user) === String(meId)) return "Вы";
    if (typeof user !== "object") return fallback;

    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return name || user.username || user.email || fallback;
};

export const errorText = (error, fallback = "Не получилось. Проверьте связь и попробуйте ещё раз.") => String(
    error?.response?.data?.message
    || error?.response?.data?.error
    || (error?.message && error.message !== "Network Error" ? error.message : "")
    || fallback
);

// ---------- списки ----------

export const isListPinned = (list, meId) => !!meId && (list?.pinnedBy || []).includes(String(meId));

/** Личный список скрыть нельзя. */
export const isListHidden = (list, meId) => (
    !list?.isPersonal && !!meId && (list?.hiddenBy || []).includes(String(meId))
);

/** Порядок: личный список, потом закреплённые, дальше — где недавно что-то менялось. */
export const sortLists = (lists = [], meId = "") => [...lists].sort((a, b) => {
    if (!!a.isPersonal !== !!b.isPersonal) return a.isPersonal ? -1 : 1;
    const pinned = Number(isListPinned(b, meId)) - Number(isListPinned(a, meId));
    if (pinned) return pinned;
    return String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""));
});

/** Сколько задач в списке ещё не выполнено. */
export const activeCount = (list) => Number(list?.counts?.todo || 0) + Number(list?.counts?.in_progress || 0);

// ---------- задачи ----------

const STATUS_ORDER = { in_progress: 0, todo: 1, done: 2 };

/** Порядок задач: закреплённые → в работе → не начатые → готовые; важные выше; новые выше. */
export const sortTasks = (tasks = []) => [...tasks].sort((a, b) => {
    // Закрепление действует, пока задача не выполнена.
    const pinA = !!a.pinned && a.status !== "done";
    const pinB = !!b.pinned && b.status !== "done";
    if (pinA !== pinB) return pinA ? -1 : 1;

    const byStatus = (STATUS_ORDER[a.status] ?? 1) - (STATUS_ORDER[b.status] ?? 1);
    if (byStatus) return byStatus;

    if (a.status === "done") return String(b.completedAt || "").localeCompare(String(a.completedAt || ""));
    if (a.priority !== b.priority) return a.priority === "high" ? -1 : 1;
    return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
});

/**
 * Пришедшая версия задачи старее той, что уже на экране. Ответы сервера могут
 * прийти не по порядку — старую версию поверх новой класть нельзя.
 */
export const isOlderVersion = (incoming, shown) => (
    !!incoming?.updatedAt && !!shown?.updatedAt && String(incoming.updatedAt) < String(shown.updatedAt)
);

/** Задачи списка без подзадач и подзадачи по id задачи-родителя. */
export const splitTasks = (tasks = []) => {
    const top = [];
    const children = {};

    tasks.forEach((task) => {
        if (!task?.parentId) {
            top.push(task);
            return;
        }
        if (!children[task.parentId]) children[task.parentId] = [];
        children[task.parentId].push(task);
    });

    // Подзадачи идут в том порядке, в каком их добавляли.
    Object.values(children).forEach((items) => items.sort(
        (a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || ""))
    ));

    return { top: sortTasks(top), children };
};

// ---------- даты ----------

export const DAY_MS = 24 * 60 * 60 * 1000;
export const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const MONTH_NAMES = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

const two = (value) => String(value).padStart(2, "0");

export const toDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Ключ дня по времени компьютера: «2026-10-12». */
export const dayKey = (value) => {
    const date = toDate(value);
    return date ? `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}` : "";
};

export const timeText = (date) => `${two(date.getHours())}:${two(date.getMinutes())}`;

/** «Сегодня», «Завтра», «Вчера» или «12 октября» (год — если не текущий). */
export const dayText = (value, now = new Date()) => {
    const date = toDate(value);
    if (!date) return "";

    const diff = Math.round((startOfDay(date).getTime() - startOfDay(now).getTime()) / DAY_MS);
    if (diff === 0) return "Сегодня";
    if (diff === 1) return "Завтра";
    if (diff === -1) return "Вчера";

    const year = date.getFullYear() === now.getFullYear() ? "" : ` ${date.getFullYear()}`;
    return `${date.getDate()} ${MONTHS[date.getMonth()]}${year}`;
};

/** Срок целиком: «Завтра, 09:00». */
export const dueText = (value, now = new Date()) => {
    const date = toDate(value);
    return date ? `${dayText(date, now)}, ${timeText(date)}` : "";
};

/** Когда что-то произошло: «Сегодня, 14:05». */
export const whenText = (value, now = new Date()) => dueText(value, now);

/** Срок прошёл, а задача не выполнена. */
export const isOverdue = (task, now = new Date()) => {
    const date = toDate(task?.dueAt);
    return !!date && task.status !== "done" && date.getTime() < now.getTime();
};

/** Дата для поля <input type="datetime-local">: «2026-10-12T09:00» по времени компьютера. */
export const toInputValue = (value) => {
    const date = toDate(value);
    if (!date) return "";
    return `${dayKey(date)}T${timeText(date)}`;
};

/** Значение поля даты и времени → ISO-строка для сервера (пустое или кривое — null). */
export const fromInputValue = (value) => {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return null;

    const date = new Date(
        Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), 0, 0
    );
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** День из календаря + время «09:30» → ISO-строка. */
export const dayWithTime = (day, time = "09:00") => {
    const date = toDate(day);
    const match = String(time || "").match(/^(\d{1,2}):(\d{2})/);
    if (!date) return null;

    const hours = match ? Math.min(23, Number(match[1])) : 9;
    const minutes = match ? Math.min(59, Number(match[2])) : 0;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0).toISOString();
};

export const monthTitle = (month) => `${MONTH_NAMES[month.getMonth()]} ${month.getFullYear()}`;
export const firstOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const addMonths = (month, count) => new Date(month.getFullYear(), month.getMonth() + count, 1);

/**
 * Сетка месяца: шесть недель с понедельника, вместе с днями соседних месяцев.
 * inMonth — день относится к самому месяцу (соседние рисуются бледнее).
 */
export const buildMonthGrid = (month) => {
    const first = firstOfMonth(month);
    // getDay(): воскресенье — 0. Нам нужен понедельник первым.
    const shift = (first.getDay() + 6) % 7;

    return Array.from({ length: 42 }, (_item, index) => {
        const date = new Date(first.getFullYear(), first.getMonth(), 1 - shift + index);
        return { date, key: dayKey(date), inMonth: date.getMonth() === first.getMonth() };
    });
};

// ---------- прочее ----------

export const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} МБ`;
};

export const formatDuration = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    return `${Math.floor(total / 60)}:${two(total % 60)}`;
};

/** Текст расшифрованного голосового (пусто, если ещё не расшифровано или речь не распознана). */
export const voiceText = (attachment) => (
    attachment?.kind === "voice" && attachment.transcriptStatus === "done"
        ? String(attachment.transcriptText || "").trim()
        : ""
);

/**
 * Вся задача одним текстом: название, описание, расшифровки голосовых,
 * подзадачи и комментарии. Так её можно вставить в чат или заметки.
 */
export const buildTaskText = (task, subtasks = [], nameOf = () => "") => {
    if (!task) return "";

    const parts = [];
    const title = String(task.title || "").trim();
    if (title) parts.push(title);

    const description = String(task.description || "").trim();
    if (description) parts.push(description);

    // Название голосовой задачи берётся из её же расшифровки: дважды одно и то же не пишем.
    const flat = (value) => value.replace(/\s+/g, " ").trim().toLocaleLowerCase("ru-RU");
    (task.attachments || []).map(voiceText).filter(Boolean).forEach((text) => {
        if (title && flat(text).startsWith(flat(title))) {
            // Расшифровка длиннее названия — берём её целиком вместо названия.
            if (flat(text).length > flat(title).length) parts[0] = text;
            return;
        }
        parts.push(text);
    });

    if (subtasks.length) {
        parts.push([
            "Подзадачи:",
            ...subtasks.map((item) => `${item.status === "done" ? "✓" : "•"} ${item.title}`),
        ].join("\n"));
    }

    if ((task.comments || []).length) {
        parts.push([
            "Комментарии:",
            ...task.comments.map((item) => {
                const name = nameOf(item.author);
                return `${name ? `${name}: ` : ""}${item.text}`;
            }),
        ].join("\n"));
    }

    return parts.join("\n\n").trim();
};

export const copyToClipboard = async (text) => {
    const clean = String(text || "").trim();
    if (!clean || typeof document === "undefined") return false;

    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(clean);
            return true;
        }
    } catch (_error) {
        // Запасной способ ниже.
    }

    const textarea = document.createElement("textarea");
    textarea.value = clean;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();

    try {
        return document.execCommand("copy");
    } catch (_error) {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
};

// ---------- чаты (кому отправить задачу, кого позвать в список) ----------

export const chatTitle = (chat, meId = "") => {
    if (!chat) return "Чат";
    if (chat.chatType === "saved") return chat.title || "Сохранённое";
    if (chat.chatType === "scene_guest") return chat.title || "Чат по 3D-сцене";
    if (chat.entity?.type && chat.entity.type !== "none") return chat.entity.title || chat.title || "Обсуждение";
    if (chat.isGroup || chat.chatType === "group") return chat.title || "Общий чат";
    if (chat.user) return userName(chat.user, "", "Собеседник");

    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    const other = participants.find((item) => getId(item) !== String(meId));
    return userName(other, "", "Собеседник");
};

/**
 * Люди, которых можно позвать в список: все, с кем есть чаты. Себя не берём.
 * extra — уже добавленные участники (их может не быть ни в одном чате).
 */
export const collectPeople = (chats = [], meId = "", extra = []) => {
    const people = new Map();

    const add = (user) => {
        const id = getId(user);
        if (!id || id === String(meId) || typeof user !== "object") return;
        if (!people.has(id)) people.set(id, user);
    };

    extra.forEach(add);
    (Array.isArray(chats) ? chats : []).forEach((chat) => {
        add(chat?.user);
        (Array.isArray(chat?.participants) ? chat.participants : []).forEach(add);
    });

    return [...people.values()].sort((a, b) => userName(a).localeCompare(userName(b), "ru"));
};
